import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Send, RotateCw, Loader2, CheckCircle2, AlertCircle, Clock, X, History, FileClock, Trash2 } from 'lucide-react';
import { useRevisionDiff, RevisionDiffSummary } from './RevisionDiff';
import { toast } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = {
  not_sent: { label: 'Draft — not sent', cls: 'bg-slate-100 text-slate-700 border-slate-300', Icon: Clock },
  sending: { label: 'Sending…', cls: 'bg-blue-50 text-blue-800 border-blue-200', Icon: Loader2 },
  sent: { label: 'Sent', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  partial: { label: 'Partially sent', cls: 'bg-amber-50 text-amber-800 border-amber-300', Icon: AlertCircle },
  failed: { label: 'Send failed', cls: 'bg-red-50 text-red-800 border-red-200', Icon: AlertCircle },
  legacy: { label: 'Sent', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  superseded: { label: 'Replaced by revision', cls: 'bg-slate-100 text-slate-500 border-slate-300', Icon: History },
};

export const deliveryStatus = (payroll) => {
  if (payroll?.status === 'superseded') return 'superseded';
  return (payroll?.delivery?.status) || (payroll?.status === 'draft' ? 'not_sent' : 'legacy');
};

/** Banner shown on a released payroll that has a pending (unsent) revision, or on the pending revision draft itself. */
export const PendingRevisionNotice = ({ payroll, onDiscarded }) => {
  const navigate = useNavigate();
  const [discarding, setDiscarding] = useState(false);
  if (!payroll) return null;
  const discard = async (targetId, parentId) => {
    if (!window.confirm('Discard this pending revision?\n\nThe revised draft is deleted. The previously sent payroll stays exactly as it is (it was never modified), escrow/advance ledgers are untouched, and no driver is emailed or notified.')) return;
    setDiscarding(true);
    try {
      await axios.post(`${API}/payrolls/${targetId}/discard-revision`, { confirm: true }, { withCredentials: true, headers: { 'X-CivilX-Action': 'discard-revision' } });
      toast.success('Pending revision discarded — the sent payroll remains active.');
      onDiscarded?.();
      navigate(`/payroll/${parentId}`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not discard the revision');
    } finally { setDiscarding(false); }
  };
  const DiscardBtn = ({ targetId, parentId }) => (
    <button type="button" data-testid="discard-revision" disabled={discarding} onClick={() => discard(targetId, parentId)} className="flex items-center gap-1 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">
      {discarding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Discard revision
    </button>
  );
  if (payroll.pending_revision_id) {
    return (
      <div data-testid="pending-revision-parent-banner" className="mb-6 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <FileClock className="h-5 w-5 shrink-0 text-amber-600" />
        <p className="flex-1">A revised draft of this payroll is pending{payroll.pending_revision_by_name ? ` (prepared by ${payroll.pending_revision_by_name})` : ''}. Drivers still see <strong>this sent version</strong>, which stays financially authoritative until the revision is explicitly sent. Nothing has been emailed.</p>
        <DiscardBtn targetId={payroll.pending_revision_id} parentId={payroll.id} />
        <button type="button" data-testid="review-pending-revision" onClick={() => navigate(`/payroll/${payroll.pending_revision_id}`)} className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700">Review & send revision</button>
      </div>
    );
  }
  if (payroll.revision_of && payroll.status === 'draft') {
    return (
      <div data-testid="pending-revision-draft-banner" className="mb-6 flex flex-wrap items-center gap-3 rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <FileClock className="h-5 w-5 shrink-0 text-blue-600" />
        <p className="flex-1">This is a <strong>pending revision</strong>. Drivers still see the previously sent payroll, and its escrow/advance effects remain in force; sending this draft replaces it and applies the revised amounts exactly once (the old version is kept for history). No emails go out until you click Send payroll.</p>
        <DiscardBtn targetId={payroll.id} parentId={payroll.revision_of} />
        <button type="button" data-testid="view-released-version" onClick={() => navigate(`/payroll/${payroll.revision_of}`)} className="rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-900 hover:bg-blue-100">View sent version</button>
      </div>
    );
  }
  if (payroll.status === 'superseded') {
    return (
      <div data-testid="superseded-banner" className="mb-6 flex flex-wrap items-center gap-3 rounded-md border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <History className="h-5 w-5 shrink-0 text-slate-500" />
        <p className="flex-1">This version was replaced by a sent revision{payroll.superseded_at ? ` on ${new Date(payroll.superseded_at).toLocaleString()}` : ''}. It is kept for history and is no longer visible to drivers.</p>
        {payroll.superseded_by && <button type="button" data-testid="view-current-revision" onClick={() => navigate(`/payroll/${payroll.superseded_by}`)} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-100">Open current version</button>}
      </div>
    );
  }
  return null;
};

export const PayrollStatusBadge = ({ payroll, compact = false }) => {
  const s = deliveryStatus(payroll);
  const { label, cls, Icon } = STYLES[s] || STYLES.not_sent;
  const d = payroll?.delivery || {};
  return (
    <span data-testid="payroll-delivery-status" data-status={s} className={`inline-flex items-center gap-1 border rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>
      <Icon className={`w-3 h-3 ${s === 'sending' ? 'animate-spin' : ''}`} />
      {label}
      {!compact && d.sent_at && s !== 'not_sent' && s !== 'superseded' && (
        <span className="normal-case font-medium tracking-normal">· {d.delivered ?? 0}/{d.total ?? 0} delivered{d.sent_by_name ? ` · by ${d.sent_by_name}` : ''} · {new Date(d.sent_at).toLocaleString()}</span>
      )}
      {payroll?.pending_revision_id && (
        <span data-testid="pending-revision-chip" className="ml-1 normal-case font-semibold tracking-normal text-amber-800">· revision pending</span>
      )}
    </span>
  );
};

export const SendPayrollButton = ({ payroll, isAdmin, onSent, entryCount }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const s = deliveryStatus(payroll);
  const isRevision = Boolean(payroll?.revision_of) && payroll?.status === 'draft';
  const { diff } = useRevisionDiff(payroll?.id, open && isRevision);
  const [preflight, setPreflight] = useState(null);
  const [sendAnyway, setSendAnyway] = useState(false);
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (!open || !payroll?.id) return undefined;
    let active = true;
    setPreflight(null); setSendAnyway(false);
    axios.get(`${API}/payrolls/${payroll.id}/send-preflight`, { withCredentials: true })
      .then((r) => { if (active) { setPreflight(r.data); setReason(r.data.suggested_reason || ''); } })
      .catch(() => { if (active) setPreflight({ earlier_unsent: [], is_revision: false, revision_reason_required: false }); });
    return () => { active = false; };
  }, [open, payroll?.id]);
  if (!isAdmin || !payroll || payroll.pending_revision_id) return null;
  const retry = s === 'partial' || s === 'failed';
  if (!(s === 'not_sent' || retry)) return null;
  const pending = retry ? Math.max(0, (payroll.delivery?.total ?? 0) - (payroll.delivery?.delivered ?? 0) - (payroll.delivery?.skipped ?? 0)) : null;

  const send = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/payrolls/${payroll.id}/send`, { confirm: true, retry, send_anyway: sendAnyway, revision_reason: isRevision ? reason.trim() : undefined }, { withCredentials: true, headers: { 'X-CivilX-Action': 'send-payroll' } });
      const d = res.data.delivery;
      if (d.status === 'sent') toast.success(`Payroll sent — ${d.delivered}/${d.total} paystub emails delivered${d.skipped ? ` (${d.skipped} skipped)` : ''}.`);
      else if (d.status === 'partial') toast.warning(`Payroll released; ${d.failed} email(s) failed — use Retry failed.`);
      else toast.error('Payroll released but no emails could be delivered — check the email settings and retry.');
      setOpen(false);
      onSent?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === 'earlier_unsent' || detail?.code === 'revision_reason_required') {
        toast.error(detail.message);
      } else if (detail?.code === 'ledgers_changed') {
        toast.error(detail.message, { duration: 12000, description: 'Nothing was sent. Use Recalculate, review the refreshed figures, then send.' });
        setOpen(false);
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Send failed');
      }
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" data-testid={retry ? 'retry-send-payroll' : 'send-payroll'} onClick={() => setOpen(true)} disabled={busy || s === 'sending'}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors disabled:opacity-50 ${retry ? 'text-amber-900 bg-amber-100 border border-amber-300 hover:bg-amber-200' : 'text-white bg-[#002FA7] hover:bg-[#00247f]'}`}>
        {retry ? <RotateCw className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
        {retry ? `Retry failed (${pending})` : 'Send payroll'}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="send-payroll-dialog">
          <div className={`w-full ${isRevision ? 'max-w-lg' : 'max-w-md'} rounded-xl bg-white shadow-2xl`}>
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <h2 className="text-base font-semibold text-slate-900">{retry ? 'Retry failed paystub emails?' : 'Send payroll to drivers?'}</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="send-payroll-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-5 py-4 text-sm text-slate-700 space-y-2">
              <p>Pay period <strong>{payroll.week_start} → {payroll.week_end}</strong> · {(payroll.delivery?.total ?? entryCount ?? payroll.driver_count ?? 0)} paystub{(payroll.delivery?.total ?? entryCount ?? payroll.driver_count ?? 0) === 1 ? '' : 's'}.</p>
              {retry
                ? <p>Only the {pending} paystub(s) that were not delivered will be emailed again. Drivers who already received their paystub will not get a duplicate.</p>
                : <p>This makes the payroll visible in every driver's My Pay (web and mobile), sends an in-app notification, and emails each driver their paystub PDF. This cannot be undone and will not be repeated automatically.</p>}
              {payroll.revision_of && !retry && <p className="text-xs font-semibold text-blue-900">This is a revision: sending it replaces the previously sent payroll for drivers and applies the revised escrow/advance amounts exactly once (the old version is kept for history).</p>}
              {isRevision && !retry && <RevisionDiffSummary diff={diff} />}
              {isRevision && !retry && (
                <label className="block text-xs font-semibold text-slate-700">Revision reason <span className="text-red-600">*</span> <span className="font-normal text-slate-500">(printed on each driver's revised paystub)</span>
                  <input data-testid="send-revision-reason" type="text" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Late load added for Mar 8" className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-normal" />
                </label>
              )}
              {!retry && preflight?.earlier_unsent?.length > 0 && (
                <div data-testid="send-order-warning" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <p className="font-semibold flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> {preflight.earlier_unsent.length} earlier pay week{preflight.earlier_unsent.length === 1 ? ' is' : 's are'} still an unsent draft:</p>
                  <ul className="mt-1 list-disc pl-5">{preflight.earlier_unsent.map((w) => <li key={w.payroll_id}>{w.week_start} → {w.week_end}{w.payroll_week_number ? ` (week ${w.payroll_week_number})` : ''}</li>)}</ul>
                  <p className="mt-1">Payrolls normally go out in order. Those weeks will <strong>not</strong> be sent or changed by this action. Your decision is recorded in the audit log.</p>
                  <label className="mt-2 flex items-center gap-2 font-semibold"><input data-testid="send-anyway-checkbox" type="checkbox" checked={sendAnyway} onChange={(e) => setSendAnyway(e.target.checked)} /> Send anyway — I understand earlier weeks remain unsent</label>
                </div>
              )}
              {!isRevision && !retry && <p className="text-xs text-slate-600">Escrow, advance and deduction ledgers are updated only now, when the payroll is sent. If they changed since this draft was prepared, sending is blocked until you recalculate.</p>}
              <p className="text-xs text-slate-500">Review every amount before sending — recalculating afterwards creates a pending revision that must be sent again.</p>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-100" data-testid="send-payroll-cancel">Cancel</button>
              <button type="button" onClick={send} disabled={busy || (!retry && (!preflight || (preflight.earlier_unsent?.length > 0 && !sendAnyway) || (isRevision && reason.trim().length < 3)))} className="flex items-center gap-2 rounded-lg bg-[#002FA7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#00247f] disabled:opacity-50" data-testid="send-payroll-confirm">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}{retry ? 'Retry now' : 'Yes, send payroll'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
